import React, { useEffect, useState } from 'react'
import Modal from 'react-responsive-modal'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.css'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ModalFooter from '../../components/ModalFooter/ModalFooter'
import {
  updateSubgroupNew,
  createSubgroupNew,
  getSubgroupOwnerHistory,
} from '../../actions/directoryBasicEducation'
import moment from 'moment'
import Button from '../../components/Button'
import MainTab from './MainTab'
import MembersTab from './MembersTab'
import HistoryTab from './HistoryTab'

const validationSchema = Yup.object({
  main: Yup.object({
    name: Yup.string().required('обязательное поле').max(30, 'Должно быть не более 30 символов'),
  }),
})

export default function ModalComponent({ data, closeModal, height, addMode, filters, electiveCourse }) {
  const dispatch = useDispatch()
  const { subgroupOwnerHistory } = useSelector(state => state.directoryBasicEducation)
  const [activeTab, setActiveTab] = useState(1)
  const [parallels, setParallels] = useState([])

  // Начальные значения для формы - структура по вкладкам
  const getDefaultValues = () => ({
    main: {
      name: '',
      shortname: '',
      specialization: '',
      language: '',
      study_mode: '',
      shift: '',
      track_profile_name: '',
      f_studyform: '',
      f_studysteps: '',
    },
    history: [{
      school_year_id: filters.xp_key,
      name: '',
      year: null,
      liter: '',
      term_type_id: 1
    }],
    date: moment().format('YYYY-MM-DD')
  })

  // Преобразовать значение в integer или null
  const toIntOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null
    const num = Number(value)
    return isNaN(num) ? null : num
  }

  // Собрать данные для main секции запроса
  const buildMainData = (mainValues) => ({
    cid: filters.cid,
    owner_gid: filters.gid || 0,
    school_year_id: filters.xp_key,
    name: mainValues.name,
    shortname: mainValues.shortname,
    specialization: mainValues.specialization,
    language: mainValues.language,
    study_mode: mainValues.study_mode,
    shift: toIntOrNull(mainValues.shift),
    track_profile_id: toIntOrNull(mainValues.track_profile_name),
    f_studyform: toIntOrNull(mainValues.f_studyform),
    f_studysteps: toIntOrNull(mainValues.f_studysteps),
    elective_years: electiveCourse ? parallels.map(Number) : [],
    is_elective: electiveCourse
  })

  // Подготовить данные истории для отправки
  const prepareHistoryData = (history, mainName) => {
    return history.map(h => ({
      school_year_id: toIntOrNull(h.school_year_id) || filters.xp_key,
      name: h.name || mainName || '',
      year: toIntOrNull(h.year) || 0,
      liter: h.liter || '',
      term_type_id: toIntOrNull(h.term_type_id) || 1
    }))
  }
  // Обработчик обновления - новая структура {main, history}
  const handleUpdate = (formValues) => {
    const mainData = buildMainData(formValues.main)
    const historyData = prepareHistoryData(formValues.history, formValues.main.name)
    const refreshParams = {
      school_year_id: filters.xp_key,
      cid: filters.cid,
      gid: filters.gid,
      is_elective: electiveCourse,
      show_closed: false
    }
    dispatch(updateSubgroupNew(data.gid, mainData, historyData, refreshParams))
    closeModal()
  }

  // Обработчик создания - новая структура {main, history}
  const handleCreateLocal = (formValues) => {
    const mainData = buildMainData(formValues.main)
    const historyData = prepareHistoryData(formValues.history, formValues.main.name)
    const refreshParams = {
      school_year_id: filters.xp_key,
      cid: filters.cid,
      gid: filters.gid,
      is_elective: electiveCourse,
      show_closed: false
    }

    dispatch(createSubgroupNew(mainData, historyData, refreshParams))
    closeModal()
  }
  const {
    values,
    errors,
    touched,
    submitCount,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: getDefaultValues(),
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema,
    onSubmit: (formValues) => {
      if (addMode) {
        handleCreateLocal(formValues)
      } else {
        handleUpdate(formValues)
      }
    }
  })
  // Функция для получения ошибки с поддержкой вложенных путей
  const error = (fieldName) => {
    const parts = fieldName.split('.')
    let touchedValue = touched
    let errorValue = errors
    for (const part of parts) {
      touchedValue = touchedValue?.[part]
      errorValue = errorValue?.[part]
    }
    return (touchedValue || submitCount) && errorValue
  }

  useEffect(() => {
    setActiveTab(1)
  }, [data])

  // Автозаполнение «Истории» при создании: данные родительского класса за выбранный год
  useEffect(() => {
    if (addMode && filters.gid && !electiveCourse) {
      dispatch(getSubgroupOwnerHistory(filters.gid))
    }
  }, [addMode, filters.gid, electiveCourse, dispatch])

  useEffect(() => {
    if (!addMode || !subgroupOwnerHistory?.length) return
    const ownerRow = subgroupOwnerHistory.find(h => h.school_year === filters.xp_key)
    if (!ownerRow) return
    setFieldValue('history', [{
      school_year_id: filters.xp_key,
      name: values?.main?.name || '',
      year: ownerRow.year ?? null,
      liter: ownerRow.liter || '',
      term_type_id: ownerRow.term_type_id || 1
    }])
  }, [subgroupOwnerHistory, addMode])

  // Синхронизация названия из Главной во все строки Истории
  useEffect(() => {
    if (values?.main?.name && values?.history?.length > 0) {
      const updatedHistory = values.history.map(h => ({
        ...h,
        name: values.main.name
      }))
      // Проверяем, изменилось ли что-то, чтобы избежать бесконечного цикла
      const needsUpdate = values.history.some((h, i) => h.name !== updatedHistory[i].name)
      if (needsUpdate) {
        setFieldValue('history', updatedHistory)
      }
    }
  }, [values?.main?.name])

  const handleTab = (tab) => {
    setActiveTab(tab)
  }

  return (
    <Modal
      open={!!data}
      onClose={() => false}
      showCloseIcon={false}
      height={height}
      classNames={{
        modal: styles.custom
      }}
      center
    >
      <>
        <div className={styles.modalMain}>
          <div className={styles.tabsHeader}>
            <div className={styles.title}>{data?.gid
              ? 'Редактирование подгруппы'
              : 'Добавление подгруппы'}</div>
            <div className={styles.tabs}>
              <Button mode={activeTab === 1 ? '' : 'white'} onClick={() => handleTab(1)}>Главная</Button>
              <Button
                mode={activeTab === 2 ? '' : 'white'}
                onClick={() => handleTab(2)}
                disabled={addMode}
                title={addMode ? 'Доступно только при редактировании существующей записи' : undefined}
              >
                Состав
              </Button>
              <Button mode={activeTab === 3 ? '' : 'white'} onClick={() => handleTab(3)}>История</Button>
            </div>
          </div>
        </div>
        <div className={styles.modalLocal}>
          <div className={styles.tabWrapper}>
            {activeTab === 1 && (
              <MainTab
                values={values}
                handleChange={handleChange}
                error={error}
                filters={filters}
                electiveCourse={electiveCourse}
                parallels={parallels}
                setParallels={setParallels}
                gid={data.gid}
                addMode={addMode}
                setFieldValue={setFieldValue}
              />
            )}
            {activeTab === 2 && !addMode && (
              <MembersTab
                values={values}
                handleChange={handleChange}
                error={error}
                gid={data.gid}
                cid={filters.cid}
                parallels={parallels}
              />
            )}
            {activeTab === 3 && (
              <HistoryTab
                values={values}
                setFieldValue={setFieldValue}
                addMode={addMode}
                filters={filters}
                gid={data.gid}
              />
            )}
          </div>
        </div>

        <ModalFooter handleSubmit={() => handleSubmit()} closeModal={closeModal} />
      </>
    </Modal>
  )
}
